// src/components/common/DownloadManager.jsx
import React, { useState, useEffect } from 'react';
import { 
  Download, CheckCircle, XCircle, Clock, FileText, Eye, 
  FileImage, Maximize2, File, X, ExternalLink, Image, Video
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { publicAPI } from '../../services/api';

import FilePreviewModal from './FilePreviewModal';

// Global state for notifications
let notificationCallbacks = {};

export const setNotificationCallbacks = (callbacks) => {
  notificationCallbacks = { ...notificationCallbacks, ...callbacks };
};

const DownloadManager = () => {
  const [downloads, setDownloads] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [fullImage, setFullImage] = useState(null);
  const [previewMaterial, setPreviewMaterial] = useState(null);

  // Initialize global handlers
  useEffect(() => {
    const handleDownload = (material) => {
      addDownload(material);
    };

    const handlePreview = (material) => {
      addPreview(material);
    };

    const handleOpenFilePreview = (material) => {
      if (material) {
        setPreviewMaterial(material);
      }
    };

    // Set global handlers
    setNotificationCallbacks({
      addDownload: handleDownload,
      addPreview: handlePreview,
      openImagePreview: handleOpenFilePreview,
      openFilePreview: handleOpenFilePreview
    });

    // Set on window for global access
    window.addDownloadNotification = handleDownload;
    window.addPreviewNotification = handlePreview;
    window.openImagePreview = handleOpenFilePreview;
    window.openFilePreview = handleOpenFilePreview;

    return () => {
      window.addDownloadNotification = null;
      window.addPreviewNotification = null;
      window.openImagePreview = null;
      window.openFilePreview = null;
    };
  }, []);

  const addDownload = (material) => {
    const newDownload = {
      id: Date.now(),
      title: material.title,
      status: 'downloading',
      progress: 0,
      size: material.file?.size ? formatFileSize(material.file.size) : 'Unknown',
      type: material.file?.format || 'file',
      isImage: publicAPI.isImage(material)
    };
    
    setDownloads(prev => [newDownload, ...prev.slice(0, 4)]);
    
    // Simulate download progress
    const interval = setInterval(() => {
      setDownloads(prev => prev.map(dl => 
        dl.id === newDownload.id 
          ? { ...dl, progress: Math.min(dl.progress + 10, 100) }
          : dl
      ));
    }, 200);
    
    // Complete download after 2 seconds
    setTimeout(() => {
      clearInterval(interval);
      setDownloads(prev => prev.map(dl => 
        dl.id === newDownload.id 
          ? { ...dl, status: 'completed', progress: 100 }
          : dl
      ));
      
      // Auto-remove completed downloads after 5 seconds
      setTimeout(() => {
        setDownloads(prev => prev.filter(dl => dl.id !== newDownload.id));
      }, 5000);
    }, 2000);
  };

  const addPreview = (material) => {
    const isImage = publicAPI.isImage(material);
    const canPreview = publicAPI.canPreview(material);
    
    if (!canPreview) return;
    
    const newPreview = {
      id: Date.now(),
      title: material.title,
      type: material.file?.format || 'file',
      url: publicAPI.getPreviewUrl(material._id),
      isImage,
      size: material.file?.size ? formatFileSize(material.file.size) : 'Unknown'
    };
    
    setPreviews(prev => [newPreview, ...prev.slice(0, 2)]);
    
    // Remove preview after 5 seconds
    setTimeout(() => {
      setPreviews(prev => prev.filter(p => p.id !== newPreview.id));
    }, 5000);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  const removeDownload = (id) => {
    setDownloads(prev => prev.filter(dl => dl.id !== id));
  };

  const removePreview = (id) => {
    setPreviews(prev => prev.filter(p => p.id !== id));
  };

  const openPreviewInNewTab = (preview) => {
    window.open(preview.url, '_blank', 'noopener,noreferrer');
    removePreview(preview.id);
  };

  return (
    <>
      {/* Full Image Modal (Telegram-style) */}
      <AnimatePresence>
        {fullImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999999] bg-black flex items-center justify-center"
            onClick={() => setFullImage(null)}
          >
            <div className="relative w-full h-full flex items-center justify-center">
              {/* Close button */}
              <button
                onClick={() => setFullImage(null)}
                className="absolute top-4 right-4 z-10 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              
              {/* Download button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const link = document.createElement('a');
                  link.href = publicAPI.getDownloadUrl(fullImage._id);
                  link.download = fullImage.file?.originalName || fullImage.title;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="absolute top-4 left-4 z-10 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
              >
                <Download className="w-6 h-6" />
              </button>
              
              {/* Image */}
              <motion.img
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                src={publicAPI.getPreviewUrl(fullImage._id)}
                alt={fullImage.title}
                className="max-w-full max-h-full object-contain cursor-zoom-out"
                onClick={(e) => e.stopPropagation()}
              />
              
              {/* Caption */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-6 pt-12">
                <div className="max-w-4xl mx-auto">
                  <h3 className="text-white text-lg font-semibold mb-2">{fullImage.title}</h3>
                  <div className="flex items-center gap-4 text-white/80 text-sm">
                    <span>{fullImage.file?.format?.toUpperCase()}</span>
                    <span>•</span>
                    <span>{formatFileSize(fullImage.file?.size)}</span>
                    <span>•</span>
                    <span>{fullImage.downloads || 0} downloads</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Preview Notifications */}
      <div className="fixed top-4 right-4 z-50">
        <AnimatePresence>
          {previews.map(preview => (
            <motion.div
              key={preview.id}
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 100, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl p-4 w-80 border border-gray-200 mb-2 cursor-pointer"
              onClick={() => openPreviewInNewTab(preview)}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  {preview.isImage ? (
                    <FileImage className="w-5 h-5 text-green-500" />
                  ) : (
                    <Eye className="w-5 h-5 text-blue-500" />
                  )}
                  <h4 className="font-semibold text-gray-900">
                    {preview.isImage ? 'Image Preview' : 'File Preview'}
                  </h4>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removePreview(preview.id);
                  }}
                  className="text-gray-400 hover:text-red-500"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
              
              <p className="text-sm text-gray-700 truncate mb-2">{preview.title}</p>
              
              {preview.isImage ? (
                <div className="mt-2">
                  <div className="relative h-32 bg-gray-100 rounded-lg overflow-hidden mb-2">
                    <img
                      src={preview.url}
                      alt={preview.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.src = `data:image/svg+xml,${encodeURIComponent(`
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
                            <rect width="100" height="100" fill="#f3f4f6"/>
                            <text x="50" y="50" font-family="Arial" font-size="14" fill="#9ca3af" text-anchor="middle" dy=".3em">${preview.type.toUpperCase()}</text>
                          </svg>
                        `)}`;
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Click to view full image</span>
                    <ExternalLink className="w-3 h-3" />
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center gap-2">
                    <Clock className="w-3 h-3" />
                    <span>Click to open preview</span>
                  </div>
                  <ExternalLink className="w-4 h-4" />
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Download Notifications */}
      <div className="fixed bottom-4 right-4 z-50">
        <AnimatePresence>
          {downloads.length > 0 && (
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl p-4 w-80 border border-gray-200"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-gray-900 flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Downloads ({downloads.length})
                </h3>
                <button
                  onClick={() => setDownloads([])}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear All
                </button>
              </div>
              
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {downloads.map(download => (
                  <div key={download.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-1">
                        {download.isImage ? (
                          <FileImage className="w-4 h-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <FileText className="w-4 h-4 text-gray-600 flex-shrink-0" />
                        )}
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {download.title}
                        </span>
                      </div>
                      <button
                        onClick={() => removeDownload(download.id)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        {download.status === 'downloading' ? (
                          <>
                            <Clock className="w-3 h-3 text-blue-500" />
                            <span className="text-xs text-gray-600">Downloading...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-3 h-3 text-green-500" />
                            <span className="text-xs text-gray-600">Completed</span>
                          </>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">{download.size}</span>
                    </div>
                    
                    <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          download.status === 'completed' 
                            ? 'bg-green-500' 
                            : 'bg-blue-500'
                        }`}
                        style={{ width: `${download.progress}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Universal File Preview Modal */}
      <FilePreviewModal
        material={previewMaterial}
        isOpen={!!previewMaterial}
        onClose={() => setPreviewMaterial(null)}
      />
    </>
  );
};

export default DownloadManager;